type FacilitiesView = 'grid' | 'map';
type OperatorSource = 'dashboard' | 'facilities-grid' | 'facilities-map';

interface NavigationState {
    lastFacilitiesView: FacilitiesView;
    operatorSource: OperatorSource;
}

class NavigationService {
    private static instance: NavigationService;
    private navigationState: NavigationState;

    private constructor() {
        // Load state from localStorage or use defaults
        const savedState = localStorage.getItem('spotSyncNavigation');
        this.navigationState = savedState 
            ? JSON.parse(savedState)
            : { 
                lastFacilitiesView: 'map', // Default to map view
                operatorSource: 'facilities-map' // Default operator source
            };
    }

    public static getInstance(): NavigationService {
        if (!NavigationService.instance) {
            NavigationService.instance = new NavigationService();
        }
        return NavigationService.instance;
    }

    private saveState(): void {
        localStorage.setItem('spotSyncNavigation', JSON.stringify(this.navigationState));
    }

    // Facilities view management
    public setLastFacilitiesView(view: FacilitiesView): void {
        this.navigationState.lastFacilitiesView = view;
        this.saveState();
    }

    public getLastFacilitiesView(): FacilitiesView {
        return this.navigationState.lastFacilitiesView;
    }

    // Operator source tracking for facility navigation
    public setOperatorSource(source: OperatorSource): void {
        this.navigationState.operatorSource = source;
        this.saveState();
    }

    public getOperatorSource(): OperatorSource {
        return this.navigationState.operatorSource;
    }

    // Navigation methods for facilities view switching
    public navigateToOperator(navigate: (path: string) => void): void {
        const operatorSource = this.getOperatorSource();
        
        switch (operatorSource) {
            case 'dashboard':
                navigate('/operator/dashboard');
                break;
            case 'facilities-grid':
                navigate('/operator/facilities');
                break;
            case 'facilities-map':
            default:
                navigate('/');
                break;
        }
    }

    public navigateToFacilitiesGrid(navigate: (path: string) => void): void {
        this.setLastFacilitiesView('grid');
        navigate('/operator/facilities');
    }

    public navigateToFacilitiesMap(navigate: (path: string) => void): void {
        this.setLastFacilitiesView('map');
        navigate('/');
    }

    // Methods to track navigation to facilities and set appropriate operator source
    public async navigateToFacilityFromDashboard(navigate: (path: string) => void, facilityId: number): Promise<void> {
        this.setOperatorSource('dashboard');
        
        // Get claims and determine landing
        try {
            const { getDefaultStore } = await import('jotai');
            const { authAtom } = await import('../Data/Atoms');
            const { extractClaims, getFacilityLandingPath } = await import('../helpers/AuthorizationManager');
            
            const auth = getDefaultStore().get(authAtom);
            const claims = extractClaims(auth);
            const landingPath = getFacilityLandingPath(claims, facilityId);
            
            navigate(landingPath);
        } catch {
            navigate(`/facility/${facilityId}/overview`);
        }
    }

    public async navigateToFacilityFromGrid(navigate: (path: string) => void, facilityId: number): Promise<void> {
        this.setOperatorSource('facilities-grid');
        
        // Get claims and determine landing
        try {
            const { getDefaultStore } = await import('jotai');
            const { authAtom } = await import('../Data/Atoms');
            const { extractClaims, getFacilityLandingPath } = await import('../helpers/AuthorizationManager');
            
            const auth = getDefaultStore().get(authAtom);
            const claims = extractClaims(auth);
            const landingPath = getFacilityLandingPath(claims, facilityId);
            
            navigate(landingPath);
        } catch {
            navigate(`/facility/${facilityId}/overview`);
        }
    }

    public async navigateToFacilityFromMap(navigate: (path: string) => void, facilityId: number): Promise<void> {
        this.setOperatorSource('facilities-map');
        
        // Get claims and determine landing
        try {
            const { getDefaultStore } = await import('jotai');
            const { authAtom } = await import('../Data/Atoms');
            const { extractClaims, getFacilityLandingPath } = await import('../helpers/AuthorizationManager');
            
            const auth = getDefaultStore().get(authAtom);
            const claims = extractClaims(auth);
            const landingPath = getFacilityLandingPath(claims, facilityId);
            
            navigate(landingPath);
        } catch {
            navigate(`/facility/${facilityId}/overview`);
        }
    }

    // Legacy method name for backward compatibility
    public navigateToFacilities(navigate: (path: string) => void): void {
        this.navigateToOperator(navigate);
    }
}

export const navigationService = NavigationService.getInstance();
export type { FacilitiesView, OperatorSource }; 